/**
 * Shared hook for tenant and instance management
 *
 * This hook provides access to tenant/instance data from Redux
 * and handlers for changing tenant/instance selections.
 * Used by both the Header and General Settings page.
 */

import { useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IRootReducerState } from "../store/IRootReducer";
import { authService } from "../utils/authService";
import { saveUserPreferencesToDB } from "../services/userPreferencesService";
import {
  instanceChange as instanceChangeAction,
  tenantChange as tenantChangeAction,
} from "../store/slices/authSlice";
import { updateDashboardCurrentStitchId } from "../store/slices/dashboardSlice";
import { changedInstanceLogCreate } from "../store/instanceManagement/instanceService";
import { setSelectedTenant } from "../store/slices/tenantsSlice";
import { saveInstances, InstanceItem } from "../store/slices/instancesSlice";

export interface TenantOption {
  id: string;
  name: string;
  businessname: string;
  instances?: Array<{ id: string; name: string; displayname: string }>;
}

export type InstanceOption = InstanceItem;

export interface UseTenantInstanceReturn {
  // Data
  tenants: TenantOption[];
  instances: InstanceOption[];
  selectedTenant: { id: string; name: string } | null;
  selectedInstance: { id: string; name: string } | null;
  selectedTenantId: string;
  selectedInstanceId: string;
  selectedTenantName: string;
  selectedInstanceName: string;
  isLoading: boolean;

  // Handlers
  // persistToDb: true = save to DB (General Settings), false = session only (Header)
  handleTenantChange: (tenantId: string, persistToDb?: boolean) => Promise<void>;
  handleInstanceChange: (instanceId: string, persistToDb?: boolean) => Promise<void>;
}

/**
 * Hook to access and manage tenant/instance data
 *
 * @example
 * ```tsx
 * const {
 *   tenants,
 *   instances,
 *   selectedTenantName,
 *   selectedInstanceName,
 *   handleTenantChange,
 *   handleInstanceChange
 * } = useTenantInstance();
 * ```
 */
export function useTenantInstance(): UseTenantInstanceReturn {
  const dispatch = useDispatch();

  // Get tenant data from Redux
  const tenantsData = useSelector((state: IRootReducerState) => state.tenantsReducer?.tenantsData);

  // Get instance data from Redux
  const instancesData = useSelector(
    (state: IRootReducerState) => state.instancesReducer?.instancesData,
  );

  // Extract tenants array
  const tenants: TenantOption[] = tenantsData?.tenants || [];

  // Extract instances array — scoped to selected tenant
  const allInstances: InstanceOption[] = instancesData?.instances || [];

  // Get selected tenant
  const selectedTenant = tenantsData?.selectedTenant || null;
  const selectedTenantId = selectedTenant?.id || "";
  const selectedTenantName = selectedTenant?.name || "";

  // Filter instances to only those in the selected tenant.
  // The tenants array (from /instances/my) has nested instances per tenant.
  // Use that to scope the flat instances list.
  const selectedTenantObj = tenants.find((t) => t.id === selectedTenantId);
  const tenantInstanceIds = selectedTenantObj?.instances?.map((i) => i.id);
  const instances: InstanceOption[] = tenantInstanceIds
    ? allInstances.filter((i) => tenantInstanceIds.includes(i.id))
    : allInstances;

  // Get selected instance from instancesData
  const selectedInstanceName = instancesData?.selectedInstanceName || "";
  const selectedInstanceId = instancesData?.selectedInstanceId || "";

  // Find selected instance object
  const selectedInstance = instances.find(
    (inst) => inst.displayname === selectedInstanceName || inst.id === selectedInstanceId,
  );

  // Check if data is still loading
  const isLoading = tenants.length === 0 && instances.length === 0;

  // Reset Redux state helper
  const resetReduxState = useCallback(() => {
    dispatch(updateDashboardCurrentStitchId(undefined));
  }, [dispatch]);

  /**
   * Handle tenant change
   * @param tenantId - The new tenant ID
   * @param persistToDb - If true, saves to DB and updates Redux without redirect (General Settings).
   *                      If false, saves to localStorage and redirects (Header flow).
   */
  const handleTenantChange = useCallback(
    async (tenantId: string, persistToDb: boolean = false): Promise<void> => {
      if (tenantId === selectedTenantId) return;

      resetReduxState();

      await changedInstanceLogCreate("Tenant");
      dispatch(tenantChangeAction());

      // Find the tenant and its instances
      const tenant = tenants.find((t) => t.id === tenantId);
      const tenantInstances = (tenant as any)?.instances || [];
      const firstInstance = tenantInstances[0];
      const firstInstanceId = firstInstance?.id || "";
      const firstInstanceName = firstInstance?.displayname || firstInstance?.name || "";

      // Always save to localStorage
      authService.saveTenantId(tenantId);
      if (firstInstanceId) {
        authService.saveInstanceId(firstInstanceId);
      }

      if (persistToDb) {
        // Settings page flow: Save to DB and update Redux state without redirect
        await saveUserPreferencesToDB({
          tenantId,
          instanceId: firstInstanceId || undefined,
        });

        // Update Redux state so UI refreshes without page reload
        // Use businessname for both id lookup and display name to maintain consistency
        dispatch(
          setSelectedTenant({
            id: tenantId,
            name: tenant?.businessname || tenant?.name || "",
          }),
        );

        // Dispatch saveInstances with the full structure matching what the app expects
        dispatch(
          saveInstances({
            instances: tenantInstances,
            selectedInstanceId: firstInstanceId,
            selectedInstanceName: firstInstanceName,
          }),
        );
      } else {
        // Header flow: Redirect with new instance
        const url = window.location.href;
        const arr = url.split("/");
        const newUrl = arr[0] + "//" + arr[2] + "?instanceid=" + firstInstanceId;

        setTimeout(() => {
          window.location.href = newUrl;
        }, 100);
      }
    },
    [selectedTenantId, tenants, dispatch, resetReduxState],
  );

  /**
   * Handle instance change
   * @param instanceId - The new instance ID
   * @param persistToDb - If true, saves to DB and updates Redux without redirect (General Settings).
   *                      If false, saves to localStorage and redirects (Header flow).
   */
  const handleInstanceChange = useCallback(
    async (instanceId: string, persistToDb: boolean = false): Promise<void> => {
      if (instanceId === selectedInstanceId) return;

      resetReduxState();

      await changedInstanceLogCreate("Instance");
      dispatch(instanceChangeAction());

      // Always save to localStorage
      authService.saveInstanceId(instanceId);

      // Find the instance name
      const instance = instances.find((i) => i.id === instanceId);
      const instanceName = instance?.displayname || instance?.name || "";

      if (persistToDb) {
        // Settings page flow: Save to DB and update Redux state without redirect
        await saveUserPreferencesToDB({
          instanceId,
        });

        // Update Redux state so UI refreshes without page reload
        dispatch(
          saveInstances({
            instances,
            selectedInstanceId: instanceId,
            selectedInstanceName: instanceName,
          }),
        );
      } else {
        // Header flow: Redirect with new instance
        const url = window.location.href;
        const arr = url.split("/");
        const newUrl = arr[0] + "//" + arr[2] + "?instanceid=" + instanceId;

        setTimeout(() => {
          window.location.href = newUrl;
        }, 100);
      }
    },
    [selectedInstanceId, instances, dispatch, resetReduxState],
  );

  return {
    // Data
    tenants,
    instances,
    selectedTenant,
    selectedInstance: selectedInstance
      ? { id: selectedInstance.id, name: selectedInstance.displayname }
      : null,
    selectedTenantId,
    selectedInstanceId,
    selectedTenantName,
    selectedInstanceName,
    isLoading,

    // Handlers
    handleTenantChange,
    handleInstanceChange,
  };
}

export default useTenantInstance;
